import { VehicleWidgetDialogName } from 'Components/names';
import { dialogOpened } from 'Core/actions/dialog';
import {
  removalFromGarageRequested,
  sendFitmentTableRequest,
  sendVerifyFitmentRequest,
  setGarageRequested,
  vehicleDiscardingRequested,
} from 'Core/actions/fitmentSearch/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchFieldsSelector,
  garageDataSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
  vehicleSelectionSelector,
  verifyFitmentProductSelector,
} from 'Core/selectors/fitmentSearch/index';
import type { ReduxStateType, ReduxStoreType } from 'Core/store';
import { Vehicle, VehicleCollection } from 'Models/index';
import productConfig from 'Models/uiConfig/productConfig';
import type { VehicleFieldMap } from 'Models/vehicle';
import {
  type AutoSyncPartLoader,
  type AutoSyncVisualizer,
  createAutoSyncPartLoader,
  visualizeVehicle,
  type VisualizeVehicleParams,
} from 'Modules/autoSync/index';
import { getUriWithVehicle, queryParamsFromObject } from 'Modules/converter';
import { facetsToConvertible } from 'Modules/serverApi/common/convert';
import { verifyFitmentProduct } from 'Modules/serverApi/index';
import type { VerifyFitsValue } from 'Modules/serverApi/types';
import { isObjectRecord } from 'Utils/guards';
import { addPlatformRoute } from 'Utils/url';

type FieldMap = VehicleFieldMap;

export type FitmentSearchAPI = {
  isVehicleSelected: () => boolean;
  getVehicle: (includeInternalFields?: boolean) => FieldMap;
  getVehicleString: () => string;
  getVehicleSearchParams: () => string;
  getVehicleUrl: (url: string) => string;
  getVehicleRequestParams: () => string;
  setVehicle: (vehicle: FieldMap, sendIsolatedRequest?: boolean) => void;
  discardVehicle: () => void;
  addVehicle: (vehicle: FieldMap) => void;
  removeVehicle: (vehicle: FieldMap) => void;
  getVehicleList: (includeInternalFields?: boolean) => FieldMap[];
  setVehicleList: (vehicles: FieldMap[]) => void;
  addVehicleList: (vehicles: FieldMap[]) => void;
  verifyFitmentAsync: (
    productId?: string | FieldMap,
    vehicle?: FieldMap,
  ) => Promise<VerifyFitsValue | undefined>;
  updateVerifyFitmentWidget: (productId: number, variantId?: number) => void;
  updateFitmentTableWidget: (productId: string, variantId?: string) => Promise<void>;
  openVehicleDialog: () => void;
  createAutoSyncPartLoader: (isTire?: boolean, useAutoSyncId?: boolean) => AutoSyncPartLoader;
  autoSyncVisualizeVehicle: (params: VisualizeVehicleParams) => AutoSyncVisualizer | undefined;
};

export default function getFitmentSearchAPI(store: ReduxStoreType): FitmentSearchAPI {
  const { dispatch } = store;

  const addVehicleList = (vehicles: FieldMap[]) => {
    const state = store.getState();
    const currentVehicles = garageDataSelector(state);
    const newVehicles = createVehicleCollection(state, vehicles);
    if (
      !newVehicles.find((vehicle) => {
        const values = vehicle.fieldMap;
        const savedValues = currentVehicles.find(vehicle.equals)?.fieldMap;
        return (
          !savedValues ||
          Object.keys(values).length !== Object.keys(savedValues).length ||
          Object.entries(values).some(([field, value]) => savedValues[field] !== value)
        );
      })
    ) {
      return;
    }

    dispatch(setGarageRequested({ vehicles: currentVehicles.concat(newVehicles) }));
  };

  return {
    isVehicleSelected: () => isVehicleSelectedSelector(store.getState()),
    getVehicle: (includeInternalFields) => {
      const vehicle = selectedVehicleSelector(store.getState());
      return includeInternalFields ? vehicle.fieldMap : vehicle.filteredFieldMap;
    },
    getVehicleString: () => selectedVehicleSelector(store.getState()).toString(),
    getVehicleSearchParams: () => {
      const selection = vehicleSelectionSelector(store.getState());
      return selection.map((value) => `${value.field}/${value.term}`).join('/');
    },
    getVehicleUrl: (url) =>
      getUriWithVehicle(addPlatformRoute(url), vehicleSelectionSelector(store.getState())),
    getVehicleRequestParams: () => {
      const selection = vehicleSelectionSelector(store.getState());

      if (!selection.length) {
        return '';
      }

      return queryParamsFromObject({
        facet: facetsToConvertible({ maxCountByField: {}, selection }),
      });
    },
    setVehicle: (vehicle, sendIsolatedRequest = true) => {
      const state = store.getState();

      if (!isVehicleHasAllRequiredFields(state, Object.keys(vehicle))) {
        throwIncompleteVehicleError(state);
      }

      dispatch(
        setGarageRequested(
          {
            vehicles: garageDataSelector(state),
            selectedVehicle: new Vehicle(vehicle, fitmentSearchFieldsSelector(state)),
          },
          { sendIsolatedRequest },
        ),
      );
    },
    discardVehicle: () => {
      dispatch(vehicleDiscardingRequested({ mayDiscardValue: false }));
    },
    addVehicle: (vehicle) => addVehicleList([vehicle]),
    removeVehicle: (vehicle) => {
      const state = store.getState();

      const vehicleToRemove = new Vehicle(vehicle, fitmentSearchFieldsSelector(state));
      const selectedVehicle = selectedVehicleSelector(state);

      dispatch(
        removalFromGarageRequested(vehicleToRemove, vehicleToRemove.equalsBySelection(selectedVehicle)),
      );
    },
    getVehicleList: (includeInternalFields) =>
      garageDataSelector(store.getState()).map((vehicle) =>
        includeInternalFields ? vehicle.fieldMap : vehicle.filteredFieldMap,
      ),
    setVehicleList: (vehicles) => {
      const state = store.getState();

      const newVehicles = createVehicleCollection(state, vehicles);
      dispatch(
        setGarageRequested(
          { vehicles: newVehicles, selectedVehicle: newVehicles.last ?? Vehicle.null },
          { isVehicleAutoSelected: true },
        ),
      );
    },
    addVehicleList,
    verifyFitmentAsync: (productId, vehicle) => {
      const state = store.getState();

      // only vehicle provided
      if (isFieldMap(productId) && !vehicle) {
        return verifyFitmentAsyncInternal(state, undefined, productId);
      }

      if (productId && typeof productId !== 'string') {
        throw Error('Provided product id must be a string value');
      }

      return verifyFitmentAsyncInternal(state, productId, vehicle);
    },
    updateVerifyFitmentWidget: (productId: number, variantId?: number) => {
      if (productId) {
        const state = store.getState();
        const selectedVehicle = selectedVehicleSelector(state);

        dispatch(sendVerifyFitmentRequest({ productId, vehicle: selectedVehicle, variantId }));
      } else {
        throw new Error('Provided product id not found');
      }
    },
    openVehicleDialog: () => {
      dispatch(dialogOpened(VehicleWidgetDialogName, { withCurrentVehicle: true }));
    },
    updateFitmentTableWidget: (productId: string, variantId?: string) => {
      if (!productId) {
        throw new Error('Product id is required for update fitment table widget');
      }

      return new Promise<void>((resolve, reject) => {
        dispatch(sendFitmentTableRequest({ productId, variantId, resolve, reject }));
      });
    },
    createAutoSyncPartLoader,
    autoSyncVisualizeVehicle: visualizeVehicle,
  };
}

function createVehicleCollection(state: ReduxStateType, vehicles: FieldMap[]): VehicleCollection {
  if (vehicles.some((vehicle) => !isVehicleHasAllRequiredFields(state, Object.keys(vehicle)))) {
    throwIncompleteVehicleError(state);
  }

  return new VehicleCollection(
    vehicles.map((vehicle) => new Vehicle(vehicle, fitmentSearchFieldsSelector(state))),
  );
}

function isVehicleHasAllRequiredFields(state: ReduxStateType, fields: string[]): boolean {
  const baseFitmentSearchFields = fitmentSearchBaseFieldsSelector(state);
  return baseFitmentSearchFields.every((field) => fields.includes(field));
}

function throwIncompleteVehicleError(state: ReduxStateType): never {
  const baseFitmentSearchFields = fitmentSearchBaseFieldsSelector(state);
  throw new Error(`Vehicle must have all required fields: ${baseFitmentSearchFields.join(', ')}.`);
}

async function verifyFitmentAsyncInternal(
  state: ReduxStateType,
  productId: string = productConfig.localItemId,
  vehicle?: FieldMap,
): Promise<VerifyFitsValue | undefined> {
  const selectedVehicle = selectedVehicleSelector(state);

  const vehicleToVerify = vehicle
    ? new Vehicle(vehicle, fitmentSearchFieldsSelector(state))
    : selectedVehicle;

  if (vehicleToVerify === Vehicle.null || !productId) {
    let message = '';

    if (!productId) {
      message +=
        'Function must be used on the product page or product id must be provided as the first function parameter. ';
    }

    if (vehicleToVerify === Vehicle.null) {
      message += `Vehicle must be selected on the page or provided as the second function parameter with all required fields: ${fitmentSearchBaseFieldsSelector(
        state,
      ).join(', ')}.`;
    }

    throw new Error(message);
  }

  const productData = verifyFitmentProductSelector(state);
  const fitsTheVehicle = productData?.fitsTheVehicle;

  if (productId === productData?.id && fitsTheVehicle && vehicleToVerify.equals(selectedVehicle)) {
    return fitsTheVehicle;
  }

  return verifyFitmentProduct({
    productId,
    selection: vehicleToVerify.selection,
  }).then((item) => {
    if (!item) {
      throw new Error(
        `No product found with id: '${productId}'. Please check if product id is correct or start reindexing.`,
      );
    }

    const { fitsTheVehicle } = item;

    if (fitsTheVehicle) {
      return fitsTheVehicle;
    }

    throw new Error('Something went wrong. If this error persists, please notify Convermax.');
  });
}

function isFieldMap(value: unknown): value is FieldMap {
  return isObjectRecord(value) && Object.values(value).every((entry) => typeof entry === 'string');
}
